// Task #3041 — Avatar personale del Personal Assistant ("il tuo dipendente").
//
// Contratto dati BE↔FE (pattern pos/sale-responses.ts): schema Zod + z.infer
// condivisi, usati dalla rotta backend (validate + composizione risposta) e
// come generic di `apiClient.get/put` nel frontend.
//
// Modello: preferenza PERSONALE (per-utente, oltre al normale isolamento RLS
// di tenant) con flag di abilitazione (default OFF = icona attuale), config
// avatar DiceBear (stile + seed deterministico + opzioni tipizzate, nessun
// servizio esterno a runtime) e nome custom dell'assistente. La config avatar
// resta persistita anche a flag OFF (riattivando la si ritrova).

import { z } from 'zod';

/** Stili DiceBear v9 a tema "persona" offerti nella galleria. */
export const PA_AVATAR_STYLES = [
  'personas',
  'notionists',
  'lorelei',
  'micah',
  'adventurer',
  'open-peeps',
] as const;

export const paAvatarStyleSchema = z.enum(PA_AVATAR_STYLES);
export type PaAvatarStyle = z.infer<typeof paAvatarStyleSchema>;

/** Colore esadecimale a 6 cifre SENZA `#` (formato nativo DiceBear). */
const hexColorSchema = z
  .string()
  .regex(/^[0-9a-fA-F]{6}$/, 'Colore esadecimale a 6 cifre senza #');

/**
 * Personalizzazione di base, tipizzata in modo esplicito (no record generici):
 * incarnato/capelli/sfondo/specchiatura. Ogni stile applica solo le opzioni
 * che supporta (mappatura nel registry frontend, es. micah→baseColor,
 * open-peeps→headContrastColor).
 */
export const paAvatarOptionsSchema = z
  .object({
    backgroundColor: hexColorSchema.optional(),
    skinColor: hexColorSchema.optional(),
    hairColor: hexColorSchema.optional(),
    flip: z.boolean().optional(),
  })
  .strict();
export type PaAvatarOptions = z.infer<typeof paAvatarOptionsSchema>;

/** Config avatar completa: generazione deterministica da (style, seed, options). */
export const paAvatarConfigSchema = z
  .object({
    style: paAvatarStyleSchema,
    seed: z.string().min(1).max(64),
    options: paAvatarOptionsSchema.default({}),
  })
  .strict();
export type PaAvatarConfig = z.infer<typeof paAvatarConfigSchema>;

/** DTO risposta GET/PUT `/personal-assistant/avatar-prefs` (envelope `data`). */
export const paAvatarPrefsSchema = z.object({
  /** OFF (default) = icona attuale del PA, identica a oggi. */
  enabled: z.boolean(),
  /** Nome personale dell'assistente (null = nome configurato dell'agente). */
  assistantName: z.string().max(60).nullable(),
  /** Config scelta; null = mai configurato (il FE usa il default deterministico). */
  avatar: paAvatarConfigSchema.nullable(),
});
export type PaAvatarPrefs = z.infer<typeof paAvatarPrefsSchema>;

/**
 * Body PUT: `assistantName`/`avatar` ASSENTI (undefined) = mantieni il valore
 * persistito — così il quick-toggle del flag non azzera mai la scelta avatar
 * già fatta; `null` esplicito = azzera.
 */
export const paAvatarPrefsUpdateSchema = z
  .object({
    enabled: z.boolean(),
    assistantName: z.string().trim().max(60).nullable().optional(),
    avatar: paAvatarConfigSchema.nullable().optional(),
  })
  .strict();
export type PaAvatarPrefsUpdate = z.infer<typeof paAvatarPrefsUpdateSchema>;
